import type { Layout, Layouts } from 'react-grid-layout'
import type { IGraphBuilderConfig } from '~/types/graph-builder'
import type { GraphConfigType } from '~/types/graph-config'

import { getDataLayoutPosition, getGraphLayoutPosition } from './grid'

export const getFeatureLayoutObject = ({
  graphType = 'graph',
  id,
  idx,
  x,
  y,
}: {
  graphType?: GraphConfigType
  id: string
  idx: number
  x?: number
  y?: number
}): Layout => {
  // NOTE: on the custom dashboard, we have a mix of graphs and data blocks
  // graph are resizable, data blocks are not
  switch (graphType) {
    case 'graph':
      return { ...getGraphLayoutPosition(id, idx), isResizable: true }
    case 'data-block':
      return { ...getDataLayoutPosition(id, {}), isResizable: false }
    default:
      return { h: 1, i: id, w: 1, x: x || idx, y: y || idx }
  }
}

export const getDefaultLayout = (configs: IGraphBuilderConfig[]): Layouts => ({
  lg: configs.map(({ id, series }, idx) => {
    let graphType: GraphConfigType

    if (series.some(({ graphType: gt }) => gt === 'raw')) {
      graphType = 'data-block'
    } else {
      graphType = 'graph'
    }

    return getFeatureLayoutObject({
      graphType,
      id: id as string,
      idx,
      x: 0,
      y: 0,
    })
  }),
})

export const generateLayout = (layout: Layouts, configs: IGraphBuilderConfig[]): Layouts | undefined => {
  if (layout.lg && layout.lg.length >= configs.length) {
    return layout
  }

  const newGraphs = configs.filter(({ id }) => {
    if (!id || !layout.lg?.find(({ i }) => i === id)) {
      return true
    }
    return false
  })

  const _layouts = (size: 'md' | 'lg') =>
    newGraphs.map(({ id, series }, idx) => {
      let graphType: GraphConfigType

      if (series.some(({ graphType: gt }) => gt === 'raw')) {
        graphType = 'data-block'
      } else {
        graphType = 'graph'
      }

      return getFeatureLayoutObject({
        graphType,
        id: id as string,
        idx,
        x: 0,
        y: layout[size]?.reduce((sum, { y }) => sum + y, 0),
      })
    })

  const newLayout = {
    lg: [...(layout.lg ? layout.lg : []), ..._layouts('lg')],
    md: [...(layout.md ? layout.md : []), ..._layouts('md')],
  }

  return newLayout
}
